/**
 * Anonymous public site (Task 11/12) — the ONLY unauthenticated read surface
 * in the system (`public-site.controller.ts`'s own class doc). No auth
 * header required, no `organizationId` in the URL — the BE resolves the
 * page from the `X-Tenant-Id` header (filled in by the axios interceptor
 * from the tenant-by-domain query, see `usePublicSitePage`) plus `orgSlug`
 * and, for a trainer's own page, `trainerSlug`.
 *
 * The response shape mirrors `PublicSiteResponseDto`
 * (public-site.projector.ts) 1:1 — every field here is already on the BE's
 * own whitelist, so there is no mapper narrowing anything further, unlike
 * `publicPage.api.ts`'s authenticated Api/Ui type-pair split.
 */
import type { Locale } from '@mfw/translations';
import type { ContentTypeOptions } from '@mfw/common';

import { isDefined } from '@mfw/common';

import type { BaseApiResponse } from './types/api.types';

import { get } from './axios';

export type PublicSiteSection = {
  key: string;
  sortOrder: number;
  data: unknown;
};

export type PublicSitePageInfo = {
  slug: string;
  locale: Locale;
  headline: string | null;
  intro: string | null;
};

export type PublicSiteOrganization = {
  name: string;
  logoUrl: string | null;
  theme: Record<string, string> | null;
  /** The zone the working hours are written in (IANA name). */
  timeZone: string;
  /** Only while the owner made the phone public. `countryCode` is the ISO code of its dial prefix. */
  phone: { number: string; countryCode: string | null } | null;
  email: string | null;
};

/** Business details sales contact — `null` on a trainer's page or while hidden. */
export type PublicSiteSalesContact = {
  name: string;
  email: string | null;
  phone: string | null;
};

export type PublicSiteTrainer = {
  name: string;
  avatarUrl: string | null;
};

export type PublicSitePageResponse = {
  page: PublicSitePageInfo;
  organization: PublicSiteOrganization;
  trainer?: PublicSiteTrainer;
  sections: PublicSiteSection[];
  salesContact: PublicSiteSalesContact | null;
  /** Trainer page only (`[]` on the org page): the Terms of Use governing the trainer's content. */
  termsOfUse?: unknown;
};

export type FetchPublicSitePageArgs = {
  orgSlug: string;
  trainerSlug?: string;
};

export async function fetchPublicSitePage({
  orgSlug,
  trainerSlug,
}: FetchPublicSitePageArgs): Promise<PublicSitePageResponse | undefined> {
  const path = isDefined(trainerSlug)
    ? `/v2/public-site/${orgSlug}/${trainerSlug}`
    : `/v2/public-site/${orgSlug}`;
  const response = await get<BaseApiResponse<PublicSitePageResponse>>(path);
  return response?.data;
}

/**
 * Marketplace showcase (Task 12a/12b) — mirrors `PublicShowcaseCardDto`
 * (public-site.projector.ts) 1:1. Reads like a marketplace card (price and
 * headline details) but still carries no id and no video/document URL: its
 * preview is built from exactly these fields, never a route keyed by the
 * content.
 */
export type PublicShowcaseCard = {
  type: ContentTypeOptions;
  title: string | null;
  description: string | null;
  coverUrl: string | null;
  price: number;
  currency: string;
  level: number | null;
  durationMinutes: number | null;
  days: number | null;
  kcalPerDay: number | null;
};

// Page size for the FE's own infinite-scroll requests — independent of
// `SHOWCASE_ITEM_LIMIT` (the BE's per-request CEILING, 60): a smaller page
// here just means more, smaller round trips, not a different contract. The
// BE response carries no `total`/`hasMore` field (see `projectShowcase`'s
// doc) — a full page (`data.length === limit`) is the ONLY "there may be
// more" signal, which is why this constant is exported: the hook consuming
// this function needs the exact same number to test that equality against.
export const PUBLIC_SHOWCASE_PAGE_LIMIT = 24;

export type FetchPublicSiteShowcaseArgs = {
  orgSlug: string;
  trainerSlug?: string;
  /** 1-based page number — same convention `QueryAllDto` uses everywhere else. */
  offset: number;
  /** Only these content types (one group of the page); all when omitted. */
  types?: ContentTypeOptions[];
};

export async function fetchPublicSiteShowcase({
  orgSlug,
  trainerSlug,
  offset,
  types,
}: FetchPublicSiteShowcaseArgs): Promise<PublicShowcaseCard[]> {
  const path = isDefined(trainerSlug)
    ? `/v2/public-site/${orgSlug}/${trainerSlug}/content`
    : `/v2/public-site/${orgSlug}/content`;
  const response = await get<BaseApiResponse<PublicShowcaseCard[]>>(path, {
    query: {
      offset: String(offset),
      limit: String(PUBLIC_SHOWCASE_PAGE_LIMIT),
      ...(types?.length ? { types: types.join(',') } : {}),
    },
  });
  return response?.data ?? [];
}
